// Shape types per spec §5.2
// Yjs Y.Map<string, Y.Map> mirrors these structures at runtime.

export type ShapeType =
  | 'sticky'
  | 'rect'
  | 'ellipse'
  | 'text'
  | 'arrow'
  | 'freehand'
  | 'frame'
  | 'image';

// Per-shape text formatting overrides. Every field is optional; unset fields
// fall back to a shape-type-specific default (see canvas/textStyle.ts).
// Stored on Sticky / Rect / Ellipse / TextShape / Frame so each text-bearing
// shape can carry its own Miro-style formatting through Yjs.
//
// NOTE for TextShape: `fontSize` here OVERRIDES the top-level `TextShape.fontSize`
// when present. The legacy top-level field stays for back-compat (it predates
// `textStyle` and is still used by makeText/computeGeometry as a fallback).
export interface TextStyle {
  fontSize?: number;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  color?: string;
  align?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
}

export type StickyColor =
  | 'sand'
  | 'clay'
  | 'rose'
  | 'dusk'
  | 'fog'
  | 'sage'
  | 'moss'
  | 'ochre';

export const STICKY_COLORS: readonly StickyColor[] = [
  'sand', 'clay', 'rose', 'dusk', 'fog', 'sage', 'moss', 'ochre',
];

export interface ShapeBase {
  id: string;
  type: ShapeType;
  x: number;
  y: number;
  rotation?: number;
  authorId: string;
  createdAt: number;
  parentId?: string;
  locked?: boolean;
  // Shapes sharing the same groupId move/select/lock as a unit. Set via
  // Cmd+G; cleared via Cmd+Shift+G. Absent = ungrouped. Group membership
  // lives on the shape itself (rather than a separate group registry) so
  // grouping is a single-field write and there's no orphan record when
  // shapes are deleted.
  groupId?: string;
  // Render order. Higher = drawn later (on top). Defaults to 0 when absent
  // so shapes from before this field landed still render. New shapes assign
  // `z = max(allZ) + 1` so creation order tracks insertion. Bring/send-to-
  // front mutate this field rather than reordering Yjs' Y.Map (Y.Map keys
  // can't be reordered after the fact).
  z?: number;
}

export interface Sticky extends ShapeBase {
  type: 'sticky';
  w: number;
  h: number;
  color: StickyColor;
  text: string;
  tags?: string[];
  textStyle?: TextStyle;
}

export interface Rect extends ShapeBase {
  type: 'rect';
  w: number;
  h: number;
  fill: string;
  stroke: string;
  text?: string;
  textStyle?: TextStyle;
}

export interface Ellipse extends ShapeBase {
  type: 'ellipse';
  w: number;
  h: number;
  fill: string;
  stroke: string;
  text?: string;
  textStyle?: TextStyle;
}

export interface TextShape extends ShapeBase {
  type: 'text';
  text: string;
  fontSize: number;
  textStyle?: TextStyle;
}

export type ArrowEndpoint =
  | { kind: 'point'; x: number; y: number }
  | { kind: 'shape'; shapeId: string; anchor?: 'auto' | 'top' | 'right' | 'bottom' | 'left' };

export interface Arrow extends ShapeBase {
  type: 'arrow';
  from: ArrowEndpoint;
  to: ArrowEndpoint;
  label?: string;
  style?: 'straight' | 'bezier';
}

export interface Freehand extends ShapeBase {
  type: 'freehand';
  points: number[];
  stroke: string;
  strokeWidth: number;
}

export interface Frame extends ShapeBase {
  type: 'frame';
  w: number;
  h: number;
  label: string;
  color?: string;
  textStyle?: TextStyle;
}

// Inline image. `src` is a data URL (`data:image/<type>;base64,...`) so the
// image rides inside the Yjs document — no external asset store, no signed
// URLs. Heavy in bandwidth at the upper end; we gate user-supplied images
// behind a >1MB confirm at the call site.
export interface Image extends ShapeBase {
  type: 'image';
  w: number;
  h: number;
  src: string;
  alt?: string;
}

export type Shape = Sticky | Rect | Ellipse | TextShape | Arrow | Freehand | Frame | Image;
